import React from 'react';
import { MinusIcon, PlusIcon, Trash2Icon, Edit2Icon } from 'lucide-react';
import { CartItemType } from '../types/types';
import { useLanguage } from '../context/LanguageContext';
interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
  onEdit?: (item: CartItemType) => void;
}
export function CartItem({
  item,
  onUpdateQuantity,
  onRemove,
  onEdit
}: CartItemProps) {
  const {
    t,
    formatPriceInline,
    translateProductName,
    translateIngredient
  } = useLanguage();
  const translatedName = item.productId === 'custom_bowl' ? t('customBowl') : item.productId === 'custom_pizza' ? 'Pizza Personalizada' : translateProductName(item.productId, item.name);
  return <div className="flex flex-col gap-3 py-4 border-b border-gray-800 last:border-0" data-id="element-999">
      <div className="flex justify-between items-start gap-4" data-id="element-1000">
        <div className="flex-1" data-id="element-1001">
          <div className="flex items-center gap-2 mb-1" data-id="element-1002">
            <h4 className="font-heading font-semibold text-white text-base" data-id="element-1003">
              {translatedName}
            </h4>
            {onEdit && <button onClick={() => onEdit(item)} className="p-1.5 text-gray-400 hover:text-verde bg-surface-light rounded-lg transition-colors" title={t('edit')} data-id="element-1004">
                <Edit2Icon className="w-3.5 h-3.5" data-id="element-1005" />
              </button>}
          </div>

          <div className="text-sm text-gray-400 space-y-1" data-id="element-1006">
            {/* Size selection */}
            {item.selections.selectedSize && <p className="text-verde font-medium" data-id="element-1007">
                {item.selections.selectedSize.name}
              </p>}

            {/* Half and Half */}
            {item.selections.halfAndHalf?.enabled && <p className="text-verde font-medium" data-id="element-1008">
                Mitad: {item.selections.halfAndHalf.secondHalfName}
              </p>}

            {/* Pizza Builder Selections */}
            {item.selections.pizzaSauce && <p data-id="element-1009">Salsa: {item.selections.pizzaSauce.name}</p>}
            {item.selections.pizzaToppings && item.selections.pizzaToppings.length > 0 && <p data-id="element-1010">
                  Ingredientes:{' '}
                  {item.selections.pizzaToppings.map(t => t.name).join(', ')}
                </p>}

            {/* Bowl Builder Selections */}
            {item.selections.base && <p data-id="element-1011">
                {t('base')}:{' '}
                {translateIngredient(item.selections.base.id, item.selections.base.name)}
              </p>}
            {item.selections.proteina && <p data-id="element-1012">
                {t('protein')}:{' '}
                {translateIngredient(item.selections.proteina.id, item.selections.proteina.name)}
              </p>}
            {item.selections.toppings && item.selections.toppings.length > 0 && <p data-id="element-1013">
                  {t('toppings')}:{' '}
                  {item.selections.toppings.map(t => translateIngredient(t.id, t.name)).join(', ')}
                </p>}
            {item.selections.toppingsPremium && item.selections.toppingsPremium.length > 0 && <p data-id="element-1014">
                  {t('premium')}:{' '}
                  {item.selections.toppingsPremium.map(t => translateIngredient(t.id, t.name)).join(', ')}
                </p>}
            {item.selections.aderezos && item.selections.aderezos.length > 0 && <p data-id="element-1015">
                  {t('dressing')}:{' '}
                  {item.selections.aderezos.map(a => translateIngredient(a.id, a.name)).join(', ')}
                </p>}
            {item.selections.semillas && item.selections.semillas.length > 0 && <p data-id="element-1016">
                  {t('seeds')}:{' '}
                  {item.selections.semillas.map(s => translateIngredient(s.id, s.name)).join(', ')}
                </p>}

            {/* Comments */}
            {item.selections.comentarios && <p className="text-gray-300 italic mt-2 bg-surface-light p-2 rounded-lg border border-gray-700" data-id="element-1017">
                "{item.selections.comentarios}"
              </p>}
          </div>
        </div>

        <div className="text-right" data-id="element-1018">
          <p className="font-semibold text-verde text-base" data-id="element-1019">
            {formatPriceInline(item.subtotal * item.quantity)}
          </p>
          {item.quantity > 1 && <p className="text-xs text-gray-500 mt-1" data-id="element-1020">
              {formatPriceInline(item.subtotal)} c/u
            </p>}
        </div>
      </div>

      <div className="flex items-center justify-between mt-2" data-id="element-1021">
        <button onClick={() => onRemove(item.id)} className="text-terracota hover:text-red-400 p-2 -ml-2 transition-colors flex items-center gap-1 text-sm font-medium" data-id="element-1022">
          <Trash2Icon className="w-4 h-4" data-id="element-1023" />
          <span className="hidden sm:inline" data-id="element-1024">{t('remove')}</span>
        </button>

        <div className="flex items-center gap-4 bg-surface-light rounded-xl p-1 border border-gray-800" data-id="element-1025">
          <button onClick={() => onUpdateQuantity(item.id, -1)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1026">
            <MinusIcon className="w-4 h-4" data-id="element-1027" />
          </button>
          <span className="w-6 text-center font-semibold text-white" data-id="element-1028">
            {item.quantity}
          </span>
          <button onClick={() => onUpdateQuantity(item.id, 1)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1029">
            <PlusIcon className="w-4 h-4" data-id="element-1030" />
          </button>
        </div>
      </div>
    </div>;
}